import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Globe, Pencil, Plus, ServerOff, ShieldOff, Smartphone, Trash2, Wifi } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader, PageToolbar, SectionHeading } from "@/components/page-header";
import { StatusDot } from "@/components/status-dot";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogInset,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import type { RouterOutputs } from "@/lib/types";

export const Route = createFileRoute("/_app/admin/network")({
  beforeLoad: ({ context }) => {
    // The tRPC procedures are the authorization boundary; this only routes the UI.
    if (context.user?.role !== "admin") throw redirect({ to: "/devices" });
  },
  component: NetworkPage,
});

type NetworkStatus = RouterOutputs["network"]["status"];
type Client = NetworkStatus["clients"][number];

const LIST_CARD =
  "overflow-hidden rounded-lg bg-background-100 shadow-border divide-y divide-gray-alpha-400";

const clientName = (client: Client) => client.name || client.device?.name || "Unnamed phone";

function NetworkPage() {
  const qc = useQueryClient();
  const { data, error, isLoading } = useQuery({
    ...trpc.network.status.queryOptions(),
    refetchInterval: 10_000,
    retry: false,
  });
  const [addingProxy, setAddingProxy] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: trpc.network.status.queryKey() });

  const assign = useMutation(
    trpc.network.assign.mutationOptions({
      onSuccess: (result) => {
        qc.setQueryData(trpc.network.status.queryKey(), result);
        toast.success("Phone network updated");
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  const removeProxy = useMutation(
    trpc.network.removeProxy.mutationOptions({
      onSuccess: () => {
        toast.success("Proxy removed");
        refresh();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  return (
    <div className="flex flex-col">
      <PageHeader title="Network" meta={data && <span>{data.ssid || "Office hotspot"}</span>} />

      {isLoading ? (
        <NetworkSkeleton />
      ) : error || !data ? (
        <EmptyState
          icon={<ServerOff />}
          title="Router control is unavailable"
          description={
            error?.message ??
            "Configure ROUTER_CONTROL_URL and ROUTER_CONTROL_TOKEN on the coordinator, then reload this page."
          }
        />
      ) : (
        <>
          <div className="mb-6 flex items-start gap-3 rounded-lg bg-background-100 p-4 shadow-border">
            <ShieldOff className="mt-0.5 size-5 shrink-0 text-gray-900" />
            <div>
              <p className="font-medium text-gray-1000 text-label-14">
                New phones have no internet
              </p>
              <p className="mt-1 max-w-3xl text-copy-14 text-gray-900">
                Access follows the phone's Wi-Fi MAC address and a fixed DHCP address. An unknown
                phone can join the hotspot, but gets no internet or DNS until an admin chooses a
                proxy here. There is no direct-internet fallback.
              </p>
            </div>
          </div>

          <SectionHeading>{data.clients.length} phones on the hotspot</SectionHeading>
          {data.clients.length === 0 ? (
            <EmptyState
              icon={<Wifi />}
              title="No phones discovered"
              description="Join a phone to the hotspot. It will appear here with internet blocked."
            />
          ) : (
            <ul className={LIST_CARD}>
              {data.clients.map((client) => (
                <li
                  key={client.mac}
                  className="flex flex-wrap items-center gap-3 p-4 sm:flex-nowrap"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-900">
                    <Smartphone className="size-4" />
                  </span>
                  <div className="grid min-w-0 flex-1 gap-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-semibold text-gray-1000 text-label-14">
                        {clientName(client)}
                      </span>
                      <span className="inline-flex shrink-0 items-center gap-1.5 text-gray-900 text-label-13">
                        <StatusDot tone={client.online ? "ready" : "canceled"} />
                        {client.online ? "Connected" : "Offline"}
                      </span>
                    </div>
                    <p className="truncate text-gray-900 text-label-13-mono">
                      {client.mac} · {client.address ?? "No address yet"}
                      {client.device ? ` · ${client.device.id}` : ""}
                    </p>
                    {client.device && (
                      <p className="text-gray-900 text-label-13">
                        {client.device.matchedBy === "mac"
                          ? "Matched automatically by reported Wi-Fi MAC"
                          : "Linked manually to this farm device"}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="tertiary"
                    size="icon-sm"
                    className="shrink-0 pointer-coarse:size-10"
                    aria-label={`Edit ${clientName(client)}`}
                    onClick={() => setEditing(client)}
                  >
                    <Pencil />
                  </Button>
                  <Select
                    value={client.proxyId ?? "blocked"}
                    disabled={assign.isPending}
                    onValueChange={(proxyId) =>
                      assign.mutate({
                        mac: client.mac,
                        proxyId: proxyId === "blocked" ? null : proxyId,
                      })
                    }
                  >
                    <SelectTrigger
                      className="w-full sm:w-52"
                      aria-label={`Internet access for ${clientName(client)}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blocked">No internet</SelectItem>
                      {data.proxies.map((proxy) => (
                        <SelectItem key={proxy.id} value={proxy.id}>
                          {proxy.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </li>
              ))}
            </ul>
          )}

          <PageToolbar className="mt-10 mb-3 flex-nowrap">
            <h2 className="flex-1 font-medium text-gray-1000 text-label-14">Proxy connections</h2>
            <Button size="small" prefix={<Plus />} onClick={() => setAddingProxy(true)}>
              Add proxy
            </Button>
          </PageToolbar>
          {data.proxies.length === 0 ? (
            <EmptyState
              icon={<Globe />}
              title="No proxies configured"
              description="Add a proxy before giving a phone internet access. Credentials are stored only on the router and are never returned to this app."
            />
          ) : (
            <ul className={LIST_CARD}>
              {data.proxies.map((proxy) => {
                const count = data.clients.filter((client) => client.proxyId === proxy.id).length;
                return (
                  <li key={proxy.id} className="flex items-center gap-3 p-4">
                    <Globe className="size-4 shrink-0 text-gray-900" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-gray-1000 text-label-14">
                        {proxy.name}
                      </p>
                      <p className="mt-0.5 truncate text-gray-900 text-label-13-mono">{proxy.id}</p>
                    </div>
                    <span className="text-gray-900 text-label-14 tabular-nums">
                      {count} {count === 1 ? "phone" : "phones"}
                    </span>
                    <Button
                      variant="tertiary"
                      size="icon-sm"
                      className="shrink-0 text-red-900 pointer-coarse:size-10"
                      aria-label={`Remove ${proxy.name}`}
                      disabled={count > 0 || removeProxy.isPending}
                      onClick={() => {
                        if (confirm(`Remove proxy “${proxy.name}”?`)) {
                          removeProxy.mutate({ id: proxy.id });
                        }
                      }}
                    >
                      <Trash2 />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <AddProxyDialog open={addingProxy} onOpenChange={setAddingProxy} onSaved={refresh} />
      <EditPhoneDialog
        key={editing?.mac ?? "closed"}
        client={editing}
        onOpenChange={() => setEditing(null)}
      />
    </div>
  );
}

function NetworkSkeleton() {
  return (
    <ul className={LIST_CARD} aria-hidden>
      {[0, 1, 2].map((row) => (
        <li key={row} className="flex h-[72px] items-center gap-4 px-4">
          <Skeleton className="size-9 rounded-full" />
          <div className="grid flex-1 gap-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3.5 w-64 max-w-full" />
          </div>
          <Skeleton className="h-9 w-52" />
        </li>
      ))}
    </ul>
  );
}

function EditPhoneDialog({
  client,
  onOpenChange,
}: {
  client: Client | null;
  onOpenChange: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(client?.name ?? "");
  const [deviceId, setDeviceId] = useState(client?.deviceId ?? "");

  const save = useMutation(
    trpc.network.assign.mutationOptions({
      onSuccess: (result) => {
        qc.setQueryData(trpc.network.status.queryKey(), result);
        toast.success("Phone details saved");
        onOpenChange();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  return (
    <Dialog open={!!client} onOpenChange={(open) => !open && onOpenChange()}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Phone details</DialogTitle>
          <DialogDescription>
            Label this Wi-Fi client and optionally connect it to its farm device id. Network access
            is unchanged.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (client) save.mutate({ mac: client.mac, proxyId: client.proxyId, name, deviceId });
          }}
        >
          <DialogInset className="grid gap-5">
            <div className="grid gap-2">
              <Label htmlFor="network-phone-name">Name</Label>
              <Input
                id="network-phone-name"
                className="h-10"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="iPhone 12"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="network-device-id">Farm device id (optional)</Label>
              <Input
                id="network-device-id"
                className="h-10 font-mono"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                placeholder="00008030-…"
              />
            </div>
          </DialogInset>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" loading={save.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddProxyDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [server, setServer] = useState("");
  const [httpPort, setHttpPort] = useState("");
  const [socksPort, setSocksPort] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const save = useMutation(
    trpc.network.upsertProxy.mutationOptions({
      onSuccess: () => {
        toast.success("Proxy saved on the router");
        onSaved();
        onOpenChange(false);
        setId("");
        setName("");
        setServer("");
        setHttpPort("");
        setSocksPort("");
        setUsername("");
        setPassword("");
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-h-[90svh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add proxy</DialogTitle>
          <DialogDescription>
            TCP uses the HTTP endpoint; UDP and DNS use the authenticated SOCKS5 endpoint. These
            credentials are sent to the router once and are never readable from the farm.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            const endpoint = { server, username, password };
            save.mutate({
              id,
              name,
              http: { ...endpoint, port: Number(httpPort) },
              socks: { ...endpoint, port: Number(socksPort) },
            });
          }}
        >
          <DialogInset className="grid gap-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="proxy-id">Identifier</Label>
                <Input
                  id="proxy-id"
                  required
                  pattern="[a-z0-9][a-z0-9-]*"
                  className="h-10 font-mono"
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  placeholder="us-east"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="proxy-name">Name</Label>
                <Input
                  id="proxy-name"
                  required
                  className="h-10"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="US East"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="proxy-server">Server</Label>
              <Input
                id="proxy-server"
                required
                className="h-10 font-mono"
                value={server}
                onChange={(e) => setServer(e.target.value)}
                placeholder="192.0.2.10"
              />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="proxy-http-port">HTTP port (TCP)</Label>
                <Input
                  id="proxy-http-port"
                  required
                  type="number"
                  min={1}
                  max={65535}
                  className="h-10"
                  value={httpPort}
                  onChange={(e) => setHttpPort(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="proxy-socks-port">SOCKS5 port (UDP)</Label>
                <Input
                  id="proxy-socks-port"
                  required
                  type="number"
                  min={1}
                  max={65535}
                  className="h-10"
                  value={socksPort}
                  onChange={(e) => setSocksPort(e.target.value)}
                />
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="proxy-username">Username</Label>
                <Input
                  id="proxy-username"
                  autoComplete="off"
                  className="h-10"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="proxy-password">Password</Label>
                <Input
                  id="proxy-password"
                  type="password"
                  autoComplete="new-password"
                  className="h-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
          </DialogInset>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" loading={save.isPending}>
              Save on router
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
